import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Grid,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
  SelectChangeEvent,
  Alert,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ViewListIcon from '@mui/icons-material/ViewList';
import movMonedaService from '../../../services/movMonedaService';
import transporteService, { Transporte } from '../../../services/transporteService';
import productoService from '../../../services/productoService';
import ubicacionService from '../../../services/ubicacionService';
import { IUbicacion } from '../../../interfaces/Ubicacion';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { formatCurrency, parseLocalDate } from '../../../utils/formatters';
import { FacturaVenta, FacturaVentaDetalle } from '../../../services/facturaVentaService';
import { catalogosService } from '../../../services/catalogosService'; 
import api from '../../../services/api';
import { getApiUrl } from '../../../config/appConfig';
import inventarioInformes from '../../../services/inventarioInformes';

type FacturaVentaUI = FacturaVenta & {
  ordenCompra?: string;
  transporte?: string;
  ubicacion?: string;
  formaDePago?: string;
  nroSuc?: number;
  cuit?: string;
  dni?: string;
  tipoIVA?: string;
  idCompania?: string;
};

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
}

interface Compania {
  idCompania: string;
  nombre: string;
}

interface FacturaFormProps {
  facturaVenta: FacturaVentaUI;
  onFacturaChange: (factura: FacturaVenta) => void;
  onClienteSearch: () => void;
  onProductoSearch: () => void;
  onSave: () => void;
  loading: boolean;
  saveButtonText?: string; // Texto personalizado para el botón de guardar
  readOnly?: boolean; // Modo solo lectura (visualizar), oculta el botón Guardar
}

const FacturaForm: React.FC<FacturaFormProps> = ({
  facturaVenta,
  onFacturaChange,
  onClienteSearch,
  onProductoSearch,
  onSave,
  loading,
  saveButtonText = 'Guardar Factura', // Valor por defecto
  readOnly = false,
}) => {
  const [transportes, setTransportes] = useState<Transporte[]>([]);
  const [ubicaciones, setUbicaciones] = useState<IUbicacion[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [productos, setProductos] = useState<any[]>([]);
  const [prodSearch, setProdSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [dialogQty, setDialogQty] = useState<number>(1);
  const [dialogDiscount, setDialogDiscount] = useState<number>(0);
  const [monedaInfoDialogOpen, setMonedaInfoDialogOpen] = useState(false);
  const [subtotalInfoDialogOpen, setSubtotalInfoDialogOpen] = useState(false);
  const [selectedDetalleForInfo, setSelectedDetalleForInfo] = useState<FacturaVentaDetalle | null>(null);
  const [ramoCache, setRamoCache] = useState<Record<string, string>>({});
  const [ramoEditaPrecioCache, setRamoEditaPrecioCache] = useState<Record<string, boolean>>({});
  const [dialogPrecioLista, setDialogPrecioLista] = useState<number | null>(null);
  const [bonif1, setBonif1] = useState<number>(Number(facturaVenta.porcDesc ?? 0) || 0);
  const [bonif2, setBonif2] = useState<number>(Number(facturaVenta.porcDesc2 ?? 0) || 0);
  const [bonif3, setBonif3] = useState<number>(Number(facturaVenta.porcDesc3 ?? 0) || 0);
  const [formaDePagoDialogOpen, setFormaDePagoDialogOpen] = useState(false);
  const [ubicacionDialogOpen, setUbicacionDialogOpen] = useState(false);
  const [ubicacionSearchTerm, setUbicacionSearchTerm] = useState('');
  const [vistaProductos, setVistaProductos] = useState<'cuadrados' | 'tarjetas' | 'renglones'>('cuadrados');
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(20);
  const [productosTotalPages, setProductosTotalPages] = useState(1);
  const [cotizacionUSD, setCotizacionUSD] = useState<number | null>(null);
  const [cotizacionesCache, setCotizacionesCache] = useState<Record<number, number | null>>({});
  const [cotizacionesCargando, setCotizacionesCargando] = useState<Record<number, boolean>>({});
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [productosDataCache, setProductosDataCache] = useState<Record<string, any>>({});
  const [stockPorUbicacion, setStockPorUbicacion] = useState<Record<string, number>>({});
  const [stockInfoAnchorEl, setStockInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [stockInfoProducto, setStockInfoProducto] = useState<{ codigoArticulo: string; descripcion: string; stockTotal?: number } | null>(null);
  const [stockInfoData, setStockInfoData] = useState<{ codigoArticulo: string; descripcion: string; nroUbicacion: number; nombreUbicacion: string; cantidadDeposito: number; cantidadAsignada: number; disponibleDeposito: number }[]>([]);
  const [stockInfoLoading, setStockInfoLoading] = useState(false);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const nroSucursalSeleccionada = facturaVenta.nroSuc;
  const clienteSeleccionado = facturaVenta.nroCliente && facturaVenta.nroCliente > 0;
  const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

  // Función helper para obtener el IVA del producto según el tipo de IVA del cliente
  const getIvaRate = (producto: any): number => {
    const tipoIVA = facturaVenta.tipoIVA || '';
    const calculaIva = producto?.calculaIva;
    
    // Si el producto no calcula IVA, retornar 0
    if (calculaIva !== 'S' && calculaIva !== 's') {
      return 0;
    }

    // Mapear tipo de IVA del cliente a los campos de IVA del producto
    let ivaValue = 0;
    switch (tipoIVA) {
      case 'I': // Responsable Inscripto
        ivaValue = producto?.ivaRI ?? 0;
        break;
      case 'N': // No Categorizado
        ivaValue = producto?.ivaRN ?? 0;
        break;
      case 'E': // Exento
        ivaValue = producto?.ivaEX ?? 0;
        break;
      case 'M': // Monotributo
        ivaValue = producto?.ivaMT ?? 0;
        break;
      case 'C': // Consumidor Final
      case 'CF': // Consumidor Final (compatibilidad)
        ivaValue = producto?.ivaCF ?? 0;
        break;
      default:
        // Por defecto usar ivaRI si no hay tipo de IVA especificado
        ivaValue = producto?.ivaRI ?? 21;
    }

    // Convertir el porcentaje a decimal (ej: 21 -> 0.21)
    return Number(ivaValue) / 100;
  };

  // Función helper para obtener el multiplicador de IVA (1 + ivaRate)
  const getIvaMultiplier = (producto: any): number => {
    const ivaRate = getIvaRate(producto);
    return 1 + ivaRate;
  };

  // Función para obtener el IVA rate de un producto por código
  const getIvaRateByCodigo = (codigoArticulo: string): number => {
    const producto = productosDataCache[codigoArticulo];
    if (!producto) {
      // Si no tenemos los datos del producto, usar 21% por defecto
      return 0.21;
    }
    return getIvaRate(producto);
  };

  // Función para obtener el multiplicador de IVA por código
  const getIvaMultiplierByCodigo = (codigoArticulo: string): number => {
    const ivaRate = getIvaRateByCodigo(codigoArticulo);
    return 1 + ivaRate;
  };

  useEffect(() => {
    const nextBonif1 = clampPercent(Number(facturaVenta.porcDesc ?? 0) || 0);
    const nextBonif2 = clampPercent(Number(facturaVenta.porcDesc2 ?? 0) || 0);
    const nextBonif3 = clampPercent(Number(facturaVenta.porcDesc3 ?? 0) || 0);

    if (nextBonif1 !== bonif1) {
      setBonif1(nextBonif1);
    }
    if (nextBonif2 !== bonif2) {
      setBonif2(nextBonif2);
    }
    if (nextBonif3 !== bonif3) {
      setBonif3(nextBonif3);
    }
  }, [facturaVenta.porcDesc, facturaVenta.porcDesc2, facturaVenta.porcDesc3, bonif1, bonif2, bonif3]);

  const handleBonificacionChange = (index: 1 | 2 | 3, rawValue: string) => {
    const numericValue = clampPercent(Number(rawValue) || 0);
    if ((index === 1 && numericValue === bonif1) ||
        (index === 2 && numericValue === bonif2) ||
        (index === 3 && numericValue === bonif3)) {
      return;
    }

    const updatedFactura = {
      ...facturaVenta,
      porcDesc: index === 1 ? numericValue : (facturaVenta.porcDesc ?? bonif1 ?? 0),
      porcDesc2: index === 2 ? numericValue : (facturaVenta.porcDesc2 ?? bonif2 ?? 0),
      porcDesc3: index === 3 ? numericValue : (facturaVenta.porcDesc3 ?? bonif3 ?? 0),
    } as FacturaVenta;
    onFacturaChange(updatedFactura);

    if (index === 1) {
      setBonif1(numericValue);
    } else if (index === 2) {
      setBonif2(numericValue);
    } else {
      setBonif3(numericValue);
    }
  };

  const sucursalesOrdenadas = useMemo(() => {
    if (!sucursales.length) return [];
    return [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc);
  }, [sucursales]);
  
  const formasDePago = [
    'CONTADO',
    'CTA.CTE.',
    'EFECTIVO',
    'DEVOLUCION',
    'CONTRAREEMBOLSO'
  ];

  // Filtrar ubicaciones según el término de búsqueda
  const filteredUbicaciones = ubicaciones.filter((ubicacion) => {
    const search = ubicacionSearchTerm.toLowerCase();
    return (
      ubicacion.nombreUbicacion?.toLowerCase().includes(search) ||
      String(ubicacion.nroUbicacion)?.includes(search)
    );
  });

  useEffect(() => {
    (async () => {
      try {
        const items = await transporteService.getTransportes();
        setTransportes(items);
      } catch {}
    })();
  }, []);

  // Preseleccionar la primera compañía cargada si no hay ninguna elegida
  useEffect(() => {
    const idCompaniaActual = (facturaVenta as FacturaVentaUI).idCompania;
    if (!idCompaniaActual && companias.length > 0) {
      const primera = companias[0];
      if (primera && primera.idCompania) {
        onFacturaChange({
          ...facturaVenta,
          idCompania: primera.idCompania,
        } as FacturaVenta);
      }
    }
  }, [companias, facturaVenta, onFacturaChange]);

  useEffect(() => {
    const cargarSucursales = async () => {
      try {
        setLoadingSucursales(true);
        const response = await api.get('/api/SucursalesVenta');
        const data = response.data;
        if (data && Array.isArray(data.items)) {
          setSucursales(data.items);
        } else if (Array.isArray(data)) {
          setSucursales(data);
        } else {
          setSucursales([]);
        }
      } catch (error) {
        console.error('Error al cargar sucursales', error);
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    cargarSucursales();
  }, []);

  // Cargar compañías para el combo de compañía de la factura
  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const response = await api.get('/api/Compañias');
        const data = response.data;

        let items: Compania[] = [];
        if (Array.isArray(data)) {
          items = data as Compania[];
        } else if (data && Array.isArray(data.items)) {
          items = data.items as Compania[];
        }

        // Normalizar mínimos: recortar espacios de idCompania y nombre
        const normalizadas = items.map((c) => ({
          ...c,
          idCompania: String(c.idCompania || '').trim(),
          nombre: String(c.nombre || '').trim(),
        }));

        setCompanias(normalizadas);
      } catch (error) {
        console.error('Error al cargar compañías', error);
        setCompanias([]);
      }
    };

    cargarCompanias();
  }, []);

  useEffect(() => {
    if (
      sucursales.length &&
      (!nroSucursalSeleccionada || nroSucursalSeleccionada <= 0)
    ) {
      const primera = sucursalesOrdenadas[0] ?? sucursales[0];
      if (primera && primera.nroSuc) {
        onFacturaChange({
          ...facturaVenta,
          nroSuc: primera.nroSuc,
        } as FacturaVenta);
      }
    }
  }, [sucursales, sucursalesOrdenadas, nroSucursalSeleccionada, onFacturaChange, facturaVenta]);

  useEffect(() => {
    (async () => {
      try {
        const lista = await ubicacionService.getUbicaciones();
        setUbicaciones(Array.isArray(lista) ? lista : []);
      } catch {}
    })();
  }, []);

  // Resetear a la primera página cuando cambia la búsqueda
  useEffect(() => {
    setProductosPage(0);
  }, [prodSearch]);

  useEffect(() => {
    const fetch = async () => {
      const res = await productoService.getProductos({ 
        searchTerm: prodSearch || undefined, 
        pageNumber: productosPage + 1, 
        pageSize: productosRowsPerPage 
      });
      const items = res.items || [];
      const getDesc = (p: any) => (p.descripcion || p.descripcionArticulo || '').toString();
      const sorted = [...items].sort((a, b) => getDesc(a).localeCompare(getDesc(b), 'es', { sensitivity: 'base' }));
      
      // Almacenar los productos en el cache para tener acceso a los datos de IVA
      const newCache: Record<string, any> = {};
      sorted.forEach((p: any) => {
        const codigo = p.codigoArticulo || p.codigo;
        if (codigo) {
          newCache[codigo] = p;
        }
      });
      setProductosDataCache(prev => ({ ...prev, ...newCache }));
      
      setProductos(sorted);
      setProductosTotalPages(res.totalPages || 1);
    };
    fetch();
  }, [productosPage, productosRowsPerPage, prodSearch]);

  // Cargar stock por ubicación cuando hay ubicación seleccionada
  useEffect(() => {
    const ubicacionStr = (facturaVenta as FacturaVentaUI).ubicacion;
    const nroUbicacionStr = ubicacionStr?.match(/\((\d+)\)/)?.[1];
    const nroUbicacion = nroUbicacionStr ? parseInt(nroUbicacionStr, 10) : null;

    if (!nroUbicacion || !productos.length) {
      setStockPorUbicacion({});
      return;
    }

    const fetchStock = async () => {
      try {
        const results = await Promise.all(
          productos.map(async (p) => {
            const codigo = p.codigoArticulo || p.codigo;
            if (!codigo) return { codigo: '', disponible: 0 };
            try {
              const res = await inventarioInformes.getStockPorDeposito({
                codigoArticulo: codigo,
                nroUbicacion,
                page: 1,
                pageSize: 10,
              });
              const data = res.data || [];
              const row = data.find((r: any) => (r.codigoArticulo || '').trim() === String(codigo).trim());
              const disponible = row ? Number(row.disponibleDeposito ?? 0) : 0;
              return { codigo, disponible };
            } catch {
              return { codigo, disponible: 0 };
            }
          })
        );
        const nuevo: Record<string, number> = {};
        results.forEach((r) => {
          if (r.codigo) nuevo[r.codigo] = r.disponible;
        });
        setStockPorUbicacion(nuevo);
      } catch (error) {
        console.error('Error al cargar stock por ubicación:', error);
        setStockPorUbicacion({});
      }
    };

    fetchStock();
  }, [productos, (facturaVenta as FacturaVentaUI).ubicacion]);

  const handleStockInfoClick = (e: React.MouseEvent<HTMLElement>, codigoArticulo: string, descripcion: string, stockTotal?: number) => {
    e.stopPropagation();
    setStockInfoAnchorEl(e.currentTarget);
    setStockInfoProducto({ codigoArticulo, descripcion, stockTotal });
    setStockInfoData([]);
  };

  const handleStockInfoClose = () => {
    setStockInfoAnchorEl(null);
    setStockInfoProducto(null);
    setStockInfoData([]);
  };

  useEffect(() => {
    if (!stockInfoProducto?.codigoArticulo) return;
    const fetchStockInfo = async () => {
      setStockInfoLoading(true);
      try {
        const res = await inventarioInformes.getStockPorDeposito({
          codigoArticulo: stockInfoProducto.codigoArticulo,
          page: 1,
          pageSize: 50,
        });
        const data = (res.data || []).filter(
          (r: any) => (r.codigoArticulo || '').trim() === stockInfoProducto.codigoArticulo.trim()
        );
        setStockInfoData(data);
      } catch (error) {
        console.error('Error al cargar stock por ubicación:', error);
        setStockInfoData([]);
      } finally {
        setStockInfoLoading(false);
      }
    };
    fetchStockInfo();
  }, [stockInfoProducto?.codigoArticulo]);

  // Recalcular el importeTotal cuando cambia el tipoIVA
  useEffect(() => {
    const detallesActuales = facturaVenta.detalles || [];
    
    if (detallesActuales.length === 0) {
      if (facturaVenta.importeTotal !== 0) {
        onFacturaChange({
          ...facturaVenta,
          importeTotal: 0,
        } as FacturaVenta);
      }
      return;
    }
    
    const recalcularTotales = async () => {
      let detallesActualizados = false;
      const detallesConSubtotalesRecalculados = await Promise.all(
        detallesActuales.map(async (detalle) => {
          const codigo = detalle.codigoArticulo || '';
          let producto = productosDataCache[codigo];
          
          if (!producto && codigo) {
            try {
              producto = await productoService.getProducto(codigo);
              if (producto) {
                setProductosDataCache(prev => ({ ...prev, [codigo]: producto }));
              }
            } catch (error) {
              console.error(`Error al cargar producto ${codigo}:`, error);
            }
          }
          
          const cantidad = detalle.cantidad || 0;
          const precioUnit = detalle.precioUnitario || 0;
          const porcDesc = Number(detalle.porcDesc || 0);
          
          const ivaRate = producto ? getIvaRate(producto) : 0;
          const ivaMultiplier = 1 + ivaRate;
          
          const precioUnitConDescuento = precioUnit * (1 - porcDesc / 100);
          const precioUnitConDescuentoYIVA = precioUnitConDescuento * ivaMultiplier;
          const bonifMultiplier = (1 - (bonif1 || 0) / 100) * (1 - (bonif2 || 0) / 100) * (1 - (bonif3 || 0) / 100);
          const subtotalRecalculado = round2(cantidad * precioUnitConDescuentoYIVA * bonifMultiplier);
          
          if (detalle.subtotal !== subtotalRecalculado) {
            detallesActualizados = true;
            return {
              ...detalle,
              subtotal: subtotalRecalculado,
            };
          }
          
          return detalle;
        })
      );
      
      const importeTotal = round2(detallesConSubtotalesRecalculados.reduce((sum, det) => sum + (det.subtotal || 0), 0));
      
      if (detallesActualizados || facturaVenta.importeTotal !== importeTotal) {
        onFacturaChange({
          ...facturaVenta,
          detalles: detallesConSubtotalesRecalculados,
          importeTotal: importeTotal,
        } as FacturaVenta);
      }
    };
    
    recalcularTotales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturaVenta.tipoIVA, bonif1, bonif2, bonif3]);

  // Función helper para obtener el codigoMoneda del producto
  const getCodigoMonedaProducto = (producto: any): number | null => {
    if (!producto) return null;
    // Intentar obtener codigoMoneda desde diferentes campos posibles
    const codigoMoneda = producto.codigoMoneda ?? producto.codMoneda ?? producto.tipoMoneda ?? producto.tipoMonedaArticulo;
    const codigoMonedaNum = Number(codigoMoneda);
    // Si es 0 o null, significa ARS (no necesita cotización)
    if (!codigoMoneda || codigoMonedaNum === 0 || !Number.isFinite(codigoMonedaNum)) {
      return null;
    }
    return codigoMonedaNum;
  };

  // Función helper para obtener la cotización según el codigoMoneda del producto
  const obtenerCotizacionPorProducto = async (producto: any): Promise<number | null> => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    
    // Si no hay codigoMoneda o es 0 (ARS), no necesita cotización
    if (!codigoMoneda || codigoMoneda === 0) {
      return null;
    }

    // Si ya tenemos la cotización en caché, devolverla
    if (cotizacionesCache.hasOwnProperty(codigoMoneda)) {
      return cotizacionesCache[codigoMoneda];
    }

    // Si ya está cargando, devolver null (se actualizará cuando termine)
    if (cotizacionesCargando[codigoMoneda]) {
      return null;
    }

    // Marcar como cargando
    setCotizacionesCargando(prev => ({ ...prev, [codigoMoneda]: true }));

    try {
      // Obtener la cotización desde el servicio (usa el nuevo endpoint con fecha actual)
      const cotizacion = await movMonedaService.getUltimaCotizacionPorMoneda(codigoMoneda);
      
      // Guardar en caché (incluso si es null, para indicar que se intentó cargar)
      setCotizacionesCache(prev => ({ ...prev, [codigoMoneda]: cotizacion ?? null }));
      setCotizacionesCargando(prev => {
        const nuevo = { ...prev };
        delete nuevo[codigoMoneda];
        return nuevo;
      });

      return cotizacion ?? null;
    } catch (error) {
      console.error(`Error al obtener cotización para codigoMoneda ${codigoMoneda}:`, error);
      // Guardar null en caché para indicar que no hay cotización disponible
      setCotizacionesCache(prev => ({ ...prev, [codigoMoneda]: null }));
      setCotizacionesCargando(prev => {
        const nuevo = { ...prev };
        delete nuevo[codigoMoneda];
        return nuevo;
      });
      return null;
    }
  };

  // Función helper para obtener cotización de forma síncrona (desde caché)
  const getCotizacionPorProducto = (producto: any): number | null => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    if (!codigoMoneda || codigoMoneda === 0) {
      return null;
    }
    return cotizacionesCache[codigoMoneda] ?? null;
  };

  // Función helper para obtener el nombre de la moneda según su código
  const getNombreMoneda = (codigoMoneda: number | null, tipoMoneda?: number): string => {
    if (!codigoMoneda || codigoMoneda === 0) {
      return 'ARS';
    }
    
    // Si tipoMoneda es 1 o codigoMoneda es 1, mostrar USD
    if (codigoMoneda === 1 || tipoMoneda === 1) {
      return 'USD';
    }
    
    // Para otras monedas, mostrar el código
    return `MONEDA_${codigoMoneda}`;
  };

  // Función helper para obtener el símbolo de moneda según el tipoMoneda
  const getSimboloMoneda = (tipoMoneda: number | null | undefined): string => {
    if (!tipoMoneda || tipoMoneda === 0) {
      return '$'; // ARS por defecto
    }
    
    // Fallback: si es tipoMoneda 1, usar USD
    if (tipoMoneda === 1) {
      return 'USD';
    }
    
    return '$'; // Fallback a peso argentino
  };

  // Función helper para formatear precio con decimales personalizados
  const formatearPrecioConDecimales = (amount: number, simbolo: string, decimales: number = 2): string => {
    const formattedAmount = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales
    }).format(amount);
    return `${simbolo}${formattedAmount}`;
  };

  // Función helper para formatear precio según el tipoMoneda del producto
  const formatearPrecioPorTipoMoneda = (precio: number, producto: any, precioConvertido: boolean = false, decimales: number = 2): string => {
    // Si el precio está convertido a ARS, usar símbolo de peso
    if (precioConvertido) {
      return formatearPrecioConDecimales(precio, '$', decimales);
    }
    
    // Si no está convertido, usar el símbolo del tipoMoneda original
    const tipoMoneda = producto?.tipoMoneda ?? producto?.tipoMonedaArticulo ?? 0;
    const simbolo = getSimboloMoneda(Number(tipoMoneda));
    return formatearPrecioConDecimales(precio, simbolo, decimales);
  };

  // Función helper para calcular el precio con cotización (de forma síncrona desde caché)
  const calcularPrecioConCotizacion = (producto: any, precioBase: number, redondearIntermedio: boolean = true): { precio: number; tieneCotizacion: boolean; codigoMoneda: number | null } => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    
    // Si no hay codigoMoneda o es ARS (0), retornar precio base
    if (!codigoMoneda || codigoMoneda === 0) {
      return { precio: round2(precioBase), tieneCotizacion: false, codigoMoneda: null };
    }

    // Obtener cotización desde caché
    const cotizacion = cotizacionesCache[codigoMoneda];
    
    if (cotizacion && cotizacion > 0) {
      // Para monedas extranjeras, no redondear el precio base antes de multiplicar para mantener precisión
      const precioBaseParaCalcular = redondearIntermedio ? round2(precioBase) : precioBase;
      const precioCalculado = precioBaseParaCalcular * cotizacion;
      return { precio: redondearIntermedio ? round2(precioCalculado) : precioCalculado, tieneCotizacion: true, codigoMoneda };
    }
    
    // No hay cotización disponible (ya sea porque no está en caché o porque es null)
    return { precio: round2(precioBase), tieneCotizacion: false, codigoMoneda };
  };

  // Cargar cotización más reciente de USD al iniciar (mantener compatibilidad)
  useEffect(() => {
    (async () => {
      try {
        // Intentar obtener cotización para USD (codigoMoneda = 1)
        const cotizacionUSD = await movMonedaService.getUltimaCotizacionPorMoneda(1);
        if (cotizacionUSD && Number.isFinite(cotizacionUSD) && cotizacionUSD > 0) {
          setCotizacionUSD(cotizacionUSD);
          setCotizacionesCache(prev => ({ ...prev, 1: cotizacionUSD }));
        } else {
          setCotizacionUSD(null);
        }
      } catch {
        setCotizacionUSD(null);
      }
    })();
  }, []);

  // Cargar productos completos de los detalles cuando cambien, para tener acceso a los datos de IVA
  useEffect(() => {
    const cargarProductosDetalles = async () => {
      const codigosFaltantes = facturaVenta.detalles
        ?.map(d => d.codigoArticulo)
        .filter((codigo): codigo is string => !!codigo && !productosDataCache[codigo]) || [];
      
      if (codigosFaltantes.length > 0) {
        const productosCargados: Record<string, any> = {};
        await Promise.all(
          codigosFaltantes.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              productosCargados[codigo] = producto;
            } catch (error) {
              console.error(`Error al cargar producto ${codigo}:`, error);
            }
          })
        );
        if (Object.keys(productosCargados).length > 0) {
          setProductosDataCache(prev => ({ ...prev, ...productosCargados }));
        }
      }
    };
    
    if (facturaVenta.detalles && facturaVenta.detalles.length > 0) {
      cargarProductosDetalles();
    }
  }, [facturaVenta.detalles?.map(d => d.codigoArticulo).join(',')]);

  const applyDetalle = async (producto: any, cantidad: number, descuentoPct: number, precioListaEditado?: number | null) => {
    if (!clienteSeleccionado) {
      return;
    }
    const codigo = producto.codigoArticulo || producto.codigo;
    
    // Guardar el producto en el cache para tener acceso a los datos de IVA
    if (codigo && !productosDataCache[codigo]) {
      setProductosDataCache(prev => ({ ...prev, [codigo]: producto }));
    }
    
    const descripcion = producto.descripcion || producto.descripcionArticulo || '';
    
    let unitBase: number;
    let usarPrecioEditado = false;
    
    if (precioListaEditado !== null && precioListaEditado !== undefined && precioListaEditado > 0) {
      // Si hay precio editado, asumimos que ya está en ARS (el usuario lo ingresa en ARS)
      unitBase = precioListaEditado;
      usarPrecioEditado = true;
    } else {
      unitBase = Number(producto.precioVenta ?? producto.costo ?? producto.precio ?? producto.precioLista ?? producto.precioUnitario ?? 0);
    }
    
    let unit = unitBase;
    
    // Obtener codigoMoneda del producto y la cotización correspondiente
    const codigoMoneda = getCodigoMonedaProducto(producto);
    
    // Si el producto tiene un codigoMoneda diferente de ARS y NO estamos usando un precio editado
    // (porque el precio editado ya está en ARS), obtener la cotización
    if (!usarPrecioEditado && codigoMoneda && codigoMoneda !== 0) {
      // Intentar obtener desde caché primero
      let cotizacion = getCotizacionPorProducto(producto);
      
      // Si no está en caché, obtenerla
      if (!cotizacion) {
        cotizacion = await obtenerCotizacionPorProducto(producto);
      }
      
      // Si hay cotización válida, convertir el precio
      // No redondear intermedio para mantener precisión: precioBase * cotizacion, luego redondear al final
      if (cotizacion && cotizacion > 0) {
        unit = round2(unitBase * cotizacion);
      }
      // Si no hay cotización, mantener el precio original en la moneda del producto
    }
    // Obtener unidad de medida del producto
    const um = producto.idUnidadMedida || producto.unidadMedida?.idUnidadMedida || producto.um || '';
    const detalles = [...(facturaVenta.detalles || [])];
    const idx = detalles.findIndex(d => d.codigoArticulo === codigo);
    if (!cantidad || cantidad <= 0) {
      if (idx >= 0) {
        detalles.splice(idx, 1);
      }
    } else {
      const subtotal = cantidad * unit * (1 - (Number(descuentoPct) || 0) / 100);
      const descuentoValue = Number(descuentoPct) || 0;
      if (idx >= 0) {
        detalles[idx] = {
          ...detalles[idx],
          cantidad,
          precioUnitario: unit,
          descuento: descuentoValue,
          porcDesc: descuentoValue, // También guardar en porcDesc para compatibilidad
          subtotal,
          descripcionArticulo: descripcion,
          um: um || detalles[idx].um || '',
          codigoMoneda: 0, // Se establecerá desde la factura
          cotizacion: 0, // Se establecerá desde la factura
        };
      } else {
        detalles.push({
          nroIdformulario: facturaVenta.nroFormulario,
          nroItem: detalles.length + 1,
          codigoArticulo: codigo,
          descripcionArticulo: descripcion,
          cantidad,
          precioUnitario: unit,
          descuento: descuentoValue,
          porcDesc: descuentoValue, // También guardar en porcDesc para compatibilidad
          subtotal,
          um: um || '',
          codigoMoneda: 0, // Se establecerá desde la factura
          cotizacion: 0, // Se establecerá desde la factura
        });
      }
    }
    const importeTotal = detalles.reduce((sum, det) => sum + (det.subtotal || 0), 0);
    onFacturaChange({ ...facturaVenta, detalles, importeTotal });
  };

  const updateDetalleCantidad = async (producto: any, delta: number) => {
    if (!clienteSeleccionado) {
      return;
    }
    const codigo = producto.codigoArticulo || producto.codigo;

    const detalles = [...(facturaVenta.detalles || [])];
    const idx = detalles.findIndex(d => d.codigoArticulo === codigo);
    if (idx >= 0) {
      const nuevaCantidad = Math.max(0, (detalles[idx].cantidad || 0) + delta);
      const descuento = Number(detalles[idx].descuento ?? detalles[idx].porcDesc ?? 0);
      await applyDetalle(producto, nuevaCantidad, descuento);
      return;
    } else if (delta > 0) {
      // Si es un producto nuevo, usar el descuento del producto si existe
      const descuentoProducto = producto.porcDesc ?? producto.porc_Desc ?? producto.descuento ?? 0;
      await applyDetalle(producto, delta, Number(descuentoProducto));
      return;
    }
    const importeTotal = (facturaVenta.detalles || []).reduce((sum, det) => sum + (det.subtotal || 0), 0);
    onFacturaChange({ ...facturaVenta, importeTotal });
  };
  const openProductDialog = async (p: any) => {
    const codigo = p.codigoArticulo || p.codigo;
    
    // Cargar el producto completo si no está en el cache
    let productoCompleto = p;
    if (!productosDataCache[codigo]) {
      try {
        productoCompleto = await productoService.getProducto(codigo);
        setProductosDataCache(prev => ({ ...prev, [codigo]: productoCompleto }));
      } catch (error) {
        console.error('Error al cargar el producto completo:', error);
        // Si falla, usar el producto que ya tenemos
        productoCompleto = p;
        setProductosDataCache(prev => ({ ...prev, [codigo]: p }));
      }
    } else {
      productoCompleto = productosDataCache[codigo];
    }
    
    setSelectedProduct(productoCompleto);
    const det = facturaVenta.detalles?.find(d => d.codigoArticulo === codigo);
    setDialogQty(det?.cantidad || 1);
    // Si hay un detalle existente, usar su descuento; si no, usar el descuento del producto si existe
    const descuentoDetalle = det?.descuento ?? det?.porcDesc;
    const descuentoProducto = productoCompleto.porcDesc ?? productoCompleto.porc_Desc ?? productoCompleto.descuento;
    setDialogDiscount(Number(descuentoDetalle ?? descuentoProducto ?? 0));
    
    // Inicializar el precio de lista editable con el precio actual del detalle o del producto
    // Si hay un detalle existente, usar su precio (ya está en ARS); si no, calcular el precio base
    const precioDetalle = det?.precioUnitario;
    if (precioDetalle && precioDetalle > 0) {
      // Si hay un detalle existente, usar su precio (ya está convertido a ARS si era necesario)
      setDialogPrecioLista(precioDetalle);
    } else {
      // Si no hay detalle, calcular el precio base considerando la moneda del producto
      const precioListaBase = Number(productoCompleto.precioVenta ?? productoCompleto.precioLista ?? productoCompleto.precio ?? productoCompleto.costo ?? 0);
      const codigoMoneda = getCodigoMonedaProducto(productoCompleto);
      if (codigoMoneda && codigoMoneda !== 0) {
        // Si el producto tiene moneda especial, obtener la cotización y convertir
        let cotizacion = getCotizacionPorProducto(productoCompleto);
        if (!cotizacion && !cotizacionesCargando[codigoMoneda]) {
          cotizacion = await obtenerCotizacionPorProducto(productoCompleto);
        }
        if (cotizacion && cotizacion > 0) {
          setDialogPrecioLista(round2(precioListaBase * cotizacion));
        } else {
          setDialogPrecioLista(precioListaBase);
        }
      } else {
        setDialogPrecioLista(precioListaBase);
      }
    }
    
    // Precargar la cotización si el producto tiene un codigoMoneda diferente de ARS
    const codigoMoneda = getCodigoMonedaProducto(productoCompleto);
    if (codigoMoneda && codigoMoneda !== 0 && !cotizacionesCache.hasOwnProperty(codigoMoneda) && !cotizacionesCargando[codigoMoneda]) {
      obtenerCotizacionPorProducto(productoCompleto);
    }
    
    setProductDialogOpen(true);
  };

  const openProductDialogFromDetalle = async (detalle: FacturaVentaDetalle) => {
    const codigoArticulo = detalle.codigoArticulo;
    if (!codigoArticulo) {
      return;
    }
    // Función helper para obtener el porcentaje de descuento del detalle
    const getPorcDescFromDetalle = (d: FacturaVentaDetalle): number => {
      // Usar porcDesc (porcentaje) del backend si está disponible
      if (d.porcDesc !== undefined && d.porcDesc !== null) {
        return Number(d.porcDesc);
      }
      // Si no hay porcDesc pero hay descuento (monto) y precioUnitario, calcular el porcentaje
      if (d.descuento && d.precioUnitario && d.precioUnitario > 0) {
        return (Number(d.descuento) / Number(d.precioUnitario)) * 100;
      }
      // Si descuento es un número pequeño (< 100), asumir que ya es un porcentaje
      if (d.descuento && Number(d.descuento) < 100) {
        return Number(d.descuento);
      }
      return 0;
    };
    
    try {
      // Buscar el producto completo por código
      let producto = productosDataCache[codigoArticulo];
      if (!producto) {
        producto = await productoService.getProducto(codigoArticulo);
        setProductosDataCache(prev => ({ ...prev, [codigoArticulo]: producto }));
      }
      if (producto) {
        setSelectedProduct(producto);
        setDialogQty(detalle.cantidad || 1);
        setDialogDiscount(getPorcDescFromDetalle(detalle));
        // Inicializar el precio de lista editable con el precio del detalle
        setDialogPrecioLista(detalle.precioUnitario || 0);
        setProductDialogOpen(true);
      }
    } catch (error) {
      console.error('Error al cargar el producto:', error);
      // Si no se puede cargar el producto completo, usar la información del detalle
      // Usar precioLista del GET si está disponible, sino usar precioUnitario
      const precioLista = (detalle as any).precioLista ?? detalle.precioUnitario ?? 0;
      const productoFromDetalle = {
        codigoArticulo: codigoArticulo,
        codigo: codigoArticulo,
        descripcion: detalle.descripcionArticulo,
        descripcionArticulo: detalle.descripcionArticulo,
        precioVenta: precioLista,
        precioLista: precioLista,
        precioUnitario: precioLista,
        precio: precioLista,
      };
      setProductosDataCache(prev => ({ ...prev, [codigoArticulo]: productoFromDetalle }));
      setSelectedProduct(productoFromDetalle);
      setDialogQty(detalle.cantidad || 1);
      setDialogDiscount(getPorcDescFromDetalle(detalle));
      // Inicializar el precio de lista editable con el precio del detalle
      setDialogPrecioLista(detalle.precioUnitario || 0);
      setProductDialogOpen(true);
    }
  };
  const closeProductDialog = () => {
    setProductDialogOpen(false);
    setSelectedProduct(null);
    setDialogPrecioLista(null);
  };
  const handleActualizarProducto = async () => {
    if (!clienteSeleccionado) {
      return;
    }
    if (selectedProduct) {
      // Obtener el codigoRamo del producto para verificar si se puede editar el precio
      const codigoRamo = selectedProduct.codigoRamo;
      const puedeEditarPrecio = codigoRamo ? ramoEditaPrecioCache[codigoRamo] : false;
      
      // Si se puede editar el precio y hay un precio editado, usarlo; si no, pasar null
      const precioListaEditado = puedeEditarPrecio && dialogPrecioLista !== null && dialogPrecioLista > 0
        ? dialogPrecioLista
        : null;
      
      await applyDetalle(selectedProduct, Math.max(0, dialogQty || 0), Math.max(0, Math.min(100, dialogDiscount || 0)), precioListaEditado);
    }
    closeProductDialog();
  };

  const handleEliminarProducto = async () => {
    if (selectedProduct) {
      // Establecer cantidad en 0 para eliminar el item
      await applyDetalle(selectedProduct, 0, 0);
    }
    closeProductDialog();
  };

  // Cargar nombre de categoría (Ramo) al abrir diálogo si no está en cache
  useEffect(() => {
    const loadRamo = async () => {
      const codigoRamo = selectedProduct?.codigoRamo;
      if (!codigoRamo) return;
      
      // Cargar nombre del ramo si no está en cache
      if (!ramoCache[codigoRamo]) {
        try {
          const r = await catalogosService.getRamo(String(codigoRamo));
          setRamoCache(prev => ({ ...prev, [String(codigoRamo)]: r?.nombreRamo || String(codigoRamo) }));
        } catch {
          setRamoCache(prev => ({ ...prev, [String(codigoRamo)]: String(codigoRamo) }));
        }
      }
      
      // Cargar editaPrecio del ramo si no está en cache
      if (!ramoEditaPrecioCache.hasOwnProperty(codigoRamo)) {
        try {
          const r = await catalogosService.getRamo(String(codigoRamo));
          const editaPrecio = r?.editaPrecio === 1;
          setRamoEditaPrecioCache(prev => ({ ...prev, [String(codigoRamo)]: editaPrecio }));
        } catch {
          setRamoEditaPrecioCache(prev => ({ ...prev, [String(codigoRamo)]: false }));
        }
      }
    };
    loadRamo();
  }, [selectedProduct, ramoCache, ramoEditaPrecioCache]);

  const cantidadItems = (facturaVenta.detalles || []).length;
  const cantidadTotal = (facturaVenta.detalles || []).reduce((sum, d) => sum + (d.cantidad || 0), 0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSucursalChange = (event: SelectChangeEvent<number | string>) => {
    const value = Number(event.target.value);
    onFacturaChange({
      ...facturaVenta,
      nroSuc: Number.isFinite(value) ? value : 0,
    } as FacturaVenta);
  };

  return (
    <>
      {/* Selección de cliente - igual a Nuevo Recibo */}
      <Card sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Compañía</InputLabel>
              <Select
                label="Compañía"
                value={(facturaVenta as FacturaVentaUI).idCompania || ''}
                onChange={(e: SelectChangeEvent<string>) =>
                  onFacturaChange({
                    ...facturaVenta,
                    idCompania: e.target.value || '',
                  } as FacturaVenta)
                }
              >
                <MenuItem value="">
                  <em>Seleccione compañía</em>
                </MenuItem>
                {companias.map((c) => (
                  <MenuItem key={c.idCompania} value={c.idCompania}>
                    {c.idCompania} - {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required disabled={loadingSucursales}>
              <InputLabel>Nº Sucursal</InputLabel>
              <Select
                name="nroSuc"
                label="Nº Sucursal"
                value={facturaVenta.nroSuc && facturaVenta.nroSuc > 0 ? facturaVenta.nroSuc : ''}
                onChange={handleSucursalChange}
              >
                {loadingSucursales && (
                  <MenuItem disabled>
                    Cargando sucursales...
                  </MenuItem>
                )}
                {!loadingSucursales && sucursalesOrdenadas.map((sucursal) => (
                  <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <span>
                        {sucursal.nroSuc} - {sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Número de Cliente"
              name="nroCliente"
              value={facturaVenta.nroCliente || 0}
              onChange={(e) => onFacturaChange({
                ...facturaVenta,
                nroCliente: Number(e.target.value) || 0,
              } as FacturaVenta)}
              helperText="Escriba el número para buscar automáticamente"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={onClienteSearch} edge="end" color="primary" title="Seleccionar cliente">
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre"
              name="nombre"
              value={facturaVenta.nombre || ''}
              onChange={(e) => onFacturaChange({
                ...facturaVenta,
                nombre: e.target.value,
              } as FacturaVenta)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* Mostrar DNI si es Consumidor Final, CUIT en caso contrario */}
            {(facturaVenta.tipoIVA === 'C' || facturaVenta.tipoIVA === 'CF') ? (
              <TextField
                fullWidth
                label="DNI"
                name="dni"
                value={facturaVenta.dni || ''}
                onChange={(e) => onFacturaChange({
                  ...facturaVenta,
                  dni: e.target.value,
                } as FacturaVenta)}
              />
            ) : (
              <TextField
                fullWidth
                label="CUIT"
                name="cuit"
                value={facturaVenta.cuit || ''}
                onChange={(e) => onFacturaChange({
                  ...facturaVenta,
                  cuit: e.target.value,
                } as FacturaVenta)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Tipo IVA</InputLabel>
              <Select
                name="tipoIVA"
                value={facturaVenta.tipoIVA || ''}
                label="Tipo IVA"
                required
                onChange={(e) => onFacturaChange({
                  ...facturaVenta,
                  tipoIVA: e.target.value,
                } as FacturaVenta)}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  },
                }}
              >
                <MenuItem value="">Seleccione...</MenuItem>
                <MenuItem value="I">Responsable Inscripto</MenuItem>
                <MenuItem value="C">Consumidor Final</MenuItem>
                <MenuItem value="M">Monotributo</MenuItem>
                <MenuItem value="E">Exento</MenuItem>
                <MenuItem value="N">No Categorizado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs para las secciones */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider', minHeight: 56, px: 7 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="pestañas de factura"
          >
            <Tab label="Información de la Factura" />
            <Tab label="Catálogo de Productos" />
            <Tab label="Resumen de la Factura" />
          </Tabs>
          {/* Flechas de navegación en las esquinas de los tabs */}
          {currentTab === 0 && (
            <IconButton
              aria-label="siguiente"
              onClick={() => setCurrentTab(1)}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'primary.light',
                color: 'white',
                borderRadius: '50%',
                boxShadow: 3,
                '&:hover': { bgcolor: 'primary.main' },
                zIndex: 2
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          )}
          {currentTab === 1 && (
            <>
              <IconButton
                aria-label="anterior"
                onClick={() => setCurrentTab(0)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.light',
                  color: 'white',
                  borderRadius: '50%',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.main' },
                  zIndex: 2
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="siguiente"
                onClick={() => setCurrentTab(2)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.light',
                  color: 'white',
                  borderRadius: '50%',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.main' },
                  zIndex: 2
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </>
          )}
          {currentTab === 2 && (
            <IconButton
              aria-label="anterior"
              onClick={() => setCurrentTab(1)}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'primary.light',
                color: 'white',
                borderRadius: '50%',
                boxShadow: 3,
                '&:hover': { bgcolor: 'primary.main' },
                zIndex: 2
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Información de la factura */}
        {currentTab === 0 && (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Fecha de Emisión"
              value={facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision.toISOString().split('T')[0] : (facturaVenta.fechaEmision ? new Date(facturaVenta.fechaEmision).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])}
              onChange={(e) => onFacturaChange({
                ...facturaVenta,
                fechaEmision: parseLocalDate(e.target.value),
              } as FacturaVenta)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Orden de Compra"
              value={(facturaVenta as FacturaVentaUI).ordenCompra || ''}
              onChange={(e) => onFacturaChange({
                ...facturaVenta,
                ordenCompra: e.target.value,
              } as FacturaVenta)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Transporte</InputLabel>
              <Select
                label="Transporte"
                value={(facturaVenta as FacturaVentaUI).transporte || ''}
                onChange={(e) => onFacturaChange({
                  ...facturaVenta,
                  transporte: String(e.target.value || ''),
                } as FacturaVenta)}
              >
                <MenuItem value=""><em>Seleccione transporte</em></MenuItem>
                {transportes.map((t) => {
                  // Mostrar el nombreExpreso tal cual como viene del GET api/Transportes
                  // Con todos los espacios, sin modificaciones
                  const nombreMostrar = (t.nombreExpreso ?? t.nombre ?? '').trim();
                  const nroMostrar = String(t.nroExpreso ?? t.codigo ?? t.id ?? '');
                  return (
                    <MenuItem
                      key={String(t.id ?? t.nroExpreso ?? t.codigo)}
                      value={String(t.nroExpreso ?? t.id ?? t.codigo)}
                    >
                      {nombreMostrar + ' (' + nroMostrar + ')'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ubicación"
              value={(facturaVenta as FacturaVentaUI).ubicacion || ''}
              onClick={() => setUbicacionDialogOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setUbicacionDialogOpen(true)} edge="end" color="primary">
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Forma de Pago"
              value={(facturaVenta as FacturaVentaUI).formaDePago || ''}
              onClick={() => setFormaDePagoDialogOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setFormaDePagoDialogOpen(true)} edge="end" color="primary">
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Observaciones"
              value={facturaVenta.descripcion || ''}
              onChange={(e) => onFacturaChange({
                ...facturaVenta,
                descripcion: e.target.value,
              } as FacturaVenta)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NotesIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mt={3}>
            <Box display="flex" gap={3} alignItems="center">
              <Typography variant="body2">Artículos ({cantidadItems})</Typography>
              <Typography variant="body2">Cantidad total: {cantidadTotal}</Typography>
            </Box>
            <Typography variant="h6">Total de la Factura: {formatCurrency(facturaVenta.importeTotal || 0)}</Typography>
          </Box>
        </Box>
        )}

        {/* Catálogo de Productos */}
        {currentTab === 1 && (
        <Box sx={{ p: 2 }}>
        {!clienteSeleccionado && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Debe seleccionar un cliente antes de agregar productos al carrito.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Buscar productos..."
            value={prodSearch}
            onChange={(e) => setProdSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('cuadrados')}
              sx={{
                color: vistaProductos === 'cuadrados' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'cuadrados' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'cuadrados' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista cuadrados"
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('tarjetas')}
              sx={{
                color: vistaProductos === 'tarjetas' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'tarjetas' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'tarjetas' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista tarjetas horizontales"
            >
              <ViewAgendaIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('renglones')}
              sx={{
                color: vistaProductos === 'renglones' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'renglones' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'renglones' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista renglones"
            >
              <ViewListIcon />
            </IconButton>
          </Box>
        </Box>

        {vistaProductos === 'cuadrados' && (
          <Grid container spacing={2}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const precioBase = Number(p.precioVenta ?? p.costo ?? p.precio ?? p.precioLista ?? p.precioUnitario ?? 0);
              const detalle = facturaVenta.detalles?.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imagenCampo = p.imagenUrl || p.urlImagen || p.imageUrl || p.foto || p.imagen || null;
              const imagenPorCodigo = codigo ? `${getApiUrl()}/api/productos/${codigo}/imagen` : null;
              const imgSrc = imagenCampo || imagenPorCodigo || '/assets/sin_foto.png';
              // No redondear intermedio para monedas extranjeras para mantener precisión
              const codigoMonedaTemp = getCodigoMonedaProducto(p);
              const esMonedaEspecialTemp = codigoMonedaTemp !== null && codigoMonedaTemp !== 0;
              const { precio, tieneCotizacion, codigoMoneda } = calcularPrecioConCotizacion(p, precioBase, !esMonedaEspecialTemp);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              return (
                <Grid item xs={12} sm={6} key={codigo}>
                  <Card
                    onClick={() => openProductDialog(p)}
                    sx={{ 
                      cursor: 'pointer', 
                      borderRadius: 2, 
                      boxShadow: 1,
                      border: esMonedaEspecial ? '1px solid' : 'none',
                      borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = '/assets/sin_foto.png';
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'primary.light' }}>{codigo}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{descripcion}</Typography>
                      {esMonedaEspecial && !tieneCotizacion ? (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ color: 'warning.main', fontWeight: 700 }}>N/A</Typography>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct(p);
                              setMonedaInfoDialogOpen(true);
                            }}
                            sx={{
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              width: 20,
                              height: 20,
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <text x="3" y="15" fontSize="10" fill="#424242" fontWeight="bold">$</text>
                              <text x="17" y="15" fontSize="10" fill="#424242" fontWeight="bold">¥</text>
                              <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                              <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                              <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>{formatCurrency(precio)}</Typography>
                      )}
                      {stock !== undefined && (
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          {(facturaVenta as FacturaVentaUI).ubicacion && stockPorUbicacion[codigo] !== undefined && (
                            <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          )}
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                            <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />
                          </IconButton>
                        </Stack>
                      )}
                      <Box display="flex" alignItems="center" gap={1} mt={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField 
                          size="small" 
                          type="number" 
                          value={cantidad} 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => e.stopPropagation()} 
                          onChange={(e) => {
                            if (clienteSeleccionado && !(esMonedaEspecial && !tieneCotizacion)) {
                              const newQty = Math.max(0, Number(e.target.value) || 0);
                              const diff = newQty - cantidad;
                              if (diff !== 0) updateDetalleCantidad(p, diff);
                            }
                          }} 
                          sx={{ 
                            width: 70,
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }} 
                          inputProps={{ min: 0 }} 
                        />
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {vistaProductos === 'tarjetas' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const precioBase = Number(p.precioVenta ?? p.costo ?? p.precio ?? p.precioLista ?? p.precioUnitario ?? 0);
              const detalle = facturaVenta.detalles?.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imagenCampo = p.imagenUrl || p.urlImagen || p.imageUrl || p.foto || p.imagen || null;
              const imagenPorCodigo = codigo ? `${getApiUrl()}/api/productos/${codigo}/imagen` : null;
              const imgSrc = imagenCampo || imagenPorCodigo || '/assets/sin_foto.png';
              // No redondear intermedio para monedas extranjeras para mantener precisión
              const codigoMonedaTemp = getCodigoMonedaProducto(p);
              const esMonedaEspecialTemp = codigoMonedaTemp !== null && codigoMonedaTemp !== 0;
              const { precio, tieneCotizacion, codigoMoneda } = calcularPrecioConCotizacion(p, precioBase, !esMonedaEspecialTemp);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              return (
                <Card
                  key={codigo}
                  onClick={() => openProductDialog(p)}
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 2, 
                    boxShadow: 1, 
                    '&:hover': { boxShadow: 3 },
                    border: esMonedaEspecial ? '1px solid' : 'none',
                    borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 150,
                          height: 120,
                          minWidth: 150,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = '/assets/sin_foto.png';
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'primary.light', display: 'block', mb: 0.5 }}>{codigo}</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>{descripcion}</Typography>
                          {esMonedaEspecial && !tieneCotizacion ? (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                              <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 700 }}>N/A</Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProduct(p);
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 22,
                                  height: 22,
                                }}
                              >
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="11" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="11" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="h6" color="primary" sx={{ mb: 0.5, fontWeight: 700 }}>{formatCurrency(precio)}</Typography>
                          )}
                          {stock !== undefined && (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                              <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 24, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              {(facturaVenta as FacturaVentaUI).ubicacion && stockPorUbicacion[codigo] !== undefined && (
                                <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 24, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              )}
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                                <InfoIcon sx={{ fontSize: 18, color: 'info.main' }} />
                              </IconButton>
                            </Stack>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                          <IconButton 
                            size="small" 
                            disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                            onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField 
                            size="small" 
                            type="number" 
                            value={cantidad} 
                            disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                            onClick={(e) => e.stopPropagation()} 
                            onChange={(e) => {
                              if (clienteSeleccionado && !(esMonedaEspecial && !tieneCotizacion)) {
                                const newQty = Math.max(0, Number(e.target.value) || 0);
                                const diff = newQty - cantidad;
                                if (diff !== 0) updateDetalleCantidad(p, diff);
                              }
                            }} 
                            sx={{ 
                              width: 70,
                              '& .MuiInputBase-input.Mui-disabled': {
                                color: 'text.disabled',
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                              },
                              '& .MuiInputBase-root.Mui-disabled': {
                                bgcolor: 'grey.100',
                              }
                            }} 
                            inputProps={{ min: 0 }} 
                          />
                          <IconButton 
                            size="small" 
                            disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                            onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {vistaProductos === 'renglones' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const precioBase = Number(p.precioVenta ?? p.costo ?? p.precio ?? p.precioLista ?? p.precioUnitario ?? 0);
              const detalle = facturaVenta.detalles?.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imagenCampo = p.imagenUrl || p.urlImagen || p.imageUrl || p.foto || p.imagen || null;
              const imagenPorCodigo = codigo ? `${getApiUrl()}/api/productos/${codigo}/imagen` : null;
              const imgSrc = imagenCampo || imagenPorCodigo || '/assets/sin_foto.png';
              // No redondear intermedio para monedas extranjeras para mantener precisión
              const codigoMonedaTemp = getCodigoMonedaProducto(p);
              const esMonedaEspecialTemp = codigoMonedaTemp !== null && codigoMonedaTemp !== 0;
              const { precio, tieneCotizacion, codigoMoneda } = calcularPrecioConCotizacion(p, precioBase, !esMonedaEspecialTemp);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              return (
                <Card
                  key={codigo}
                  onClick={() => openProductDialog(p)}
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 1, 
                    boxShadow: 1, 
                    '&:hover': { boxShadow: 2 },
                    border: esMonedaEspecial ? '1px solid' : 'none',
                    borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          minWidth: 80,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = '/assets/sin_foto.png';
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'primary.light', display: 'block' }}>{codigo}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{descripcion}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                          {(esMonedaEspecial && !tieneCotizacion) ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>N/A</Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProduct(p);
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 18,
                                  height: 18,
                                }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="9" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="9" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>{formatCurrency(precio)}</Typography>
                          )}
                          {stock !== undefined && (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              {(facturaVenta as FacturaVentaUI).ubicacion && stockPorUbicacion[codigo] !== undefined && (
                                <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              )}
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                                <InfoIcon sx={{ fontSize: 14, color: 'info.main' }} />
                              </IconButton>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField 
                          size="small" 
                          type="number" 
                          value={cantidad} 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => e.stopPropagation()} 
                          onChange={(e) => {
                            if (clienteSeleccionado && !(esMonedaEspecial && !tieneCotizacion)) {
                              const newQty = Math.max(0, Number(e.target.value) || 0);
                              const diff = newQty - cantidad;
                              if (diff !== 0) updateDetalleCantidad(p, diff);
                            }
                          }} 
                          sx={{ 
                            width: 60,
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }} 
                          inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.875rem' } }} 
                        />
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !tieneCotizacion)}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Paginación en la esquina inferior */}
        {productosTotalPages > 1 && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center', 
              gap: 2, 
              mt: 3,
              pr: 2
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Página {productosPage + 1} de {productosTotalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={() => setProductosPage(prev => Math.max(0, prev - 1))}
                disabled={productosPage === 0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:disabled': {
                    borderColor: 'grey.200',
                    opacity: 0.5
                  }
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setProductosPage(prev => Math.min(productosTotalPages - 1, prev + 1))}
                disabled={productosPage >= productosTotalPages - 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:disabled': {
                    borderColor: 'grey.200',
                    opacity: 0.5
                  }
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        </Box>
        )}

        {/* Resumen de la Factura */}
        {currentTab === 2 && (
        <Box sx={{ p: 2 }}>
          <Box>
            <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Cliente:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption">{facturaVenta.nroCliente || '-'}</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Nombre del Cliente:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{facturaVenta.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Fecha de Emisión:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption">{facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision.toLocaleDateString() : (facturaVenta.fechaEmision ? new Date(facturaVenta.fechaEmision).toLocaleDateString() : '-')}</Typography>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Artículos ({(facturaVenta.detalles || []).length})
          </Typography>
          <Box display="grid" gap={1}>
            {(facturaVenta.detalles || []).map((d, idx) => {
              const cantidad = d.cantidad || 0;
              const precioUnit = d.precioUnitario || 0;
              // Usar porcDesc (porcentaje) del backend, no descuento (monto)
              // Si porcDesc no está disponible, calcularlo desde descuento y precioUnit
              let desc = Number(d.porcDesc || 0);
              if (!desc && d.descuento && precioUnit > 0) {
                // Si no hay porcDesc pero hay descuento (monto), calcular el porcentaje
                desc = (Number(d.descuento) / precioUnit) * 100;
              }
              // Asegurar que el descuento esté en el rango válido (0-100%)
              desc = Math.max(0, Math.min(100, desc));
              // Subtotal Bruto = cantidad * precioUnit * (1 - descuento/100)
              const subBruto = cantidad * precioUnit * (1 - (desc / 100));
              // Subtotal Final = cantidad * precioUnit * (1 - descuento/100) * (1 + IVA) * (1 - bonif1/100) * (1 - bonif2/100) * (1 - bonif3/100)
              // Es decir: precio unitario con descuento, luego aplicar IVA, luego aplicar bonificaciones del cliente, luego multiplicar por cantidad
              const ivaMultiplier = getIvaMultiplierByCodigo(d.codigoArticulo || '');
              const precioUnitConDescuento = precioUnit * (1 - (desc / 100));
              const precioUnitConDescuentoYIVA = precioUnitConDescuento * ivaMultiplier;
              // Aplicar bonificaciones del cliente
              const bonifMultiplier = (1 - (bonif1 || 0) / 100) * (1 - (bonif2 || 0) / 100) * (1 - (bonif3 || 0) / 100);
              const subFinal = cantidad * precioUnitConDescuentoYIVA * bonifMultiplier;
              return (
                <Box 
                  key={`${d.codigoArticulo}-${idx}`} 
                  onClick={() => openProductDialogFromDetalle(d)}
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    border: '1px solid', 
                    borderColor: 'grey.200', 
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>#{idx + 1}</Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const detalles = [...(facturaVenta.detalles || [])];
                        detalles.splice(idx, 1);
                        const importeTotal = detalles.reduce((sum, det) => sum + (det.subtotal || 0), 0);
                        onFacturaChange({
                          ...facturaVenta,
                          detalles,
                          importeTotal,
                        } as FacturaVenta);
                      }}
                      sx={{
                        color: 'error.main',
                        padding: 0.5,
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'error.dark',
                        },
                      }}
                      title="Eliminar del pedido"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.descripcionArticulo}</Typography>
                  <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={7}><Typography variant="caption">Cantidad: {cantidad}</Typography></Grid>
                    <Grid item xs={5} textAlign="right"><Typography variant="caption">Precio de Lista: {formatCurrency(precioUnit)}</Typography></Grid>
                    <Grid item xs={7}><Typography variant="caption">Código: {d.codigoArticulo}</Typography></Grid>
                    <Grid item xs={5} textAlign="right"><Typography variant="caption">Precio de Lista Final: {formatCurrency(precioUnit * ivaMultiplier)}</Typography></Grid>
                    {desc > 0 && (
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'success.main',
                            bgcolor: 'rgba(76, 175, 80, 0.1)'
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                            Descuento: {desc}%
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="body2">Subtotal Bruto:</Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>{formatCurrency(subBruto)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Subtotal Final:
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDetalleForInfo(d);
                          setSubtotalInfoDialogOpen(true);
                        }}
                        sx={{
                          color: 'warning.dark',
                          padding: 0.25,
                          '&:hover': {
                            bgcolor: 'warning.light',
                            color: 'warning.dark',
                          },
                        }}
                        title="Ver detalles del cálculo"
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 700 }}>{formatCurrency(subFinal)}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>
            Totales
          </Typography>
          <Card
            sx={{
              mt: 2,
              boxShadow: 2,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {(() => {
                // Función helper para obtener el porcentaje de descuento del detalle
                const getPorcDesc = (d: FacturaVentaDetalle): number => {
                  // Usar porcDesc (porcentaje) del backend si está disponible
                  if (d.porcDesc !== undefined && d.porcDesc !== null) {
                    return Number(d.porcDesc);
                  }
                  // Si no hay porcDesc pero hay descuento (monto) y precioUnitario, calcular el porcentaje
                  if (d.descuento && d.precioUnitario && d.precioUnitario > 0) {
                    return (Number(d.descuento) / Number(d.precioUnitario)) * 100;
                  }
                  // Si descuento es un número pequeño (< 100), asumir que ya es un porcentaje
                  if (d.descuento && Number(d.descuento) < 100) {
                    return Number(d.descuento);
                  }
                  return 0;
                };
                
                const sumaSubBruto = (facturaVenta.detalles || []).reduce((sum, d) => {
                  const porcDesc = getPorcDesc(d);
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0) * (1 - porcDesc / 100);
                  return sum + sb;
                }, 0);
                const sumaSubFinal = (facturaVenta.detalles || []).reduce((sum, d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const porcDesc = getPorcDesc(d);
                  const ivaMultiplier = getIvaMultiplierByCodigo(d.codigoArticulo || '');
                  const sf = sb * (1 - porcDesc / 100) * ivaMultiplier;
                  return sum + sf;
                }, 0);
                // Calcular Subtotal Final sin IVA para aplicar bonificaciones correctamente
                const sumaSubFinalSinIVA = (facturaVenta.detalles || []).reduce((sum, d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const porcDesc = getPorcDesc(d);
                  const sf = sb * (1 - porcDesc / 100);
                  return sum + sf;
                }, 0);
                
                // Aplicar bonificaciones sobre el subtotal sin IVA
                const totalConBonif =
                  sumaSubFinalSinIVA *
                  (1 - (bonif1 || 0) / 100) *
                  (1 - (bonif2 || 0) / 100) *
                  (1 - (bonif3 || 0) / 100);
                const totalBonificacion = sumaSubFinalSinIVA - totalConBonif;
                
                // Calcular Subtotal Neto (Subtotal Bruto - Bonificaciones)
                const subtotalNeto = sumaSubBruto - totalBonificacion;
                
                // Aplicar bonificaciones al total con descuentos pero sin IVA
                const totalConBonifYDescuentos = sumaSubFinalSinIVA *
                  (1 - (bonif1 || 0) / 100) *
                  (1 - (bonif2 || 0) / 100) *
                  (1 - (bonif3 || 0) / 100);
                
                // Agrupar detalles por tasa de IVA y calcular impuestos por grupo
                const impuestosPorTasa = new Map<number, { tasa: number; monto: number }>();
                
                (facturaVenta.detalles || []).forEach((d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const porcDesc = getPorcDesc(d);
                  const subConDescuento = sb * (1 - porcDesc / 100);
                  
                  // Calcular la proporción de este detalle en el total sin IVA
                  const proporcion = sumaSubFinalSinIVA > 0 ? subConDescuento / sumaSubFinalSinIVA : 0;
                  
                  // Aplicar bonificaciones proporcionalmente
                  const subConBonif = totalConBonifYDescuentos * proporcion;
                  
                  // Obtener la tasa de IVA
                  const ivaRate = getIvaRateByCodigo(d.codigoArticulo || '');
                  const tasaIVA = ivaRate * 100; // Convertir a porcentaje
                  
                  // Calcular el monto de IVA para este detalle
                  const montoIVA = subConBonif * ivaRate;
                  
                  // Agrupar por tasa de IVA
                  if (impuestosPorTasa.has(tasaIVA)) {
                    const existente = impuestosPorTasa.get(tasaIVA)!;
                    existente.monto += montoIVA;
                  } else {
                    impuestosPorTasa.set(tasaIVA, { tasa: tasaIVA, monto: montoIVA });
                  }
                });
                
                // Convertir el Map a un array ordenado por tasa
                const impuestosArray = Array.from(impuestosPorTasa.values())
                  .filter(imp => imp.tasa > 0) // Solo mostrar impuestos con tasa > 0
                  .sort((a, b) => b.tasa - a.tasa); // Ordenar de mayor a menor
                
                // Calcular el total de IVA
                const montoIVA = impuestosArray.reduce((sum, imp) => sum + imp.monto, 0);
                const totalFinal = totalConBonifYDescuentos + montoIVA;

                return (
                  <>
                    {/* Subtotales generales */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Subtotal Bruto:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', fontWeight: 700 }}
                        >
                          {formatCurrency(sumaSubBruto)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Bonificaciones compuestas (3 casilleros) */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.light',
                        bgcolor: 'rgba(76, 175, 80, 0.02)',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: 'success.main',
                          fontWeight: 800,
                        }}
                      >
                        Bonificación Aplicada
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          type="number"
                          value={bonif1}
                          onChange={(e) => handleBonificacionChange(1, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={bonif2}
                          onChange={(e) => handleBonificacionChange(2, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={bonif3}
                          onChange={(e) => handleBonificacionChange(3, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.main', fontWeight: 700 }}
                        >
                          Total Bonificación Aplicada:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.main', fontWeight: 800 }}
                        >
                          {formatCurrency(totalBonificacion)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Subtotal Neto */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Subtotal Neto:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', fontWeight: 700 }}
                        >
                          {formatCurrency(subtotalNeto)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Impuestos */}
                    {impuestosArray.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'info.light',
                          bgcolor: 'rgba(33, 150, 243, 0.02)',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            color: 'info.main',
                            fontWeight: 800,
                          }}
                        >
                          Impuestos
                        </Typography>
                        {impuestosArray.map((imp, index) => (
                          <Box 
                            key={index}
                            display="flex" 
                            justifyContent="space-between"
                            sx={{ mb: index < impuestosArray.length - 1 ? 0.5 : 0 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'info.main', fontWeight: 700 }}
                            >
                              IVA {imp.tasa % 1 === 0 ? imp.tasa.toFixed(0) : imp.tasa.toFixed(1)}%:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'info.main', fontWeight: 800 }}
                            >
                              {formatCurrency(imp.monto)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Totales finales similares a la app móvil */}
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 900, color: 'warning.dark' }}
                        >
                          Total Final:
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 900, color: 'warning.dark' }}
                        >
                          {formatCurrency(totalFinal)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </CardContent>
          </Card>
          </Box>
        </Box>
        )}
      </Card>

      <Dialog
        open={productDialogOpen}
        onClose={closeProductDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Detalle de producto</Typography>
          {selectedProduct && (() => {
            const codigo = selectedProduct.codigoArticulo || selectedProduct.codigo;
            const detalleExiste = facturaVenta.detalles?.find(d => d.codigoArticulo === codigo);
            if (detalleExiste) {
              return (
                <IconButton
                  onClick={handleEliminarProducto}
                  size="small"
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.dark',
                    },
                  }}
                  title="Eliminar del pedido"
                >
                  <DeleteIcon />
                </IconButton>
              );
            }
            return null;
          })()}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {selectedProduct && (
            <>
              <Box display="flex" gap={2} alignItems="flex-start" mb={2}>
                {(() => {
                  const codigo = selectedProduct.codigoArticulo || selectedProduct.codigo;
                  const desc = selectedProduct.descripcion || selectedProduct.descripcionArticulo || '';
                  const imgField = selectedProduct.imagenUrl || selectedProduct.urlImagen || selectedProduct.imageUrl || selectedProduct.foto || selectedProduct.imagen || null;
                  const imgCode = codigo ? `${getApiUrl()}/api/productos/${codigo}/imagen` : null;
                  const src = imgField || imgCode || '/assets/sin_foto.png';
                  return (
                    <Box sx={{ width: 120, height: 120, bgcolor: 'grey.100', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <CardMedia 
                        component="img" 
                        src={src} 
                        alt={desc} 
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e: any) => {
                          e.currentTarget.src = '/assets/sin_foto.png';
                        }}
                      />
                    </Box>
                  );
                })()}
                <Box flex={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, textTransform: 'uppercase' }}>
                    {selectedProduct.descripcion || selectedProduct.descripcionArticulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Código: {selectedProduct.codigoArticulo || selectedProduct.codigo}</Typography>
                  <Box mt={1}>
                    {(() => {
                      const listaBase = Number(selectedProduct.precioVenta ?? selectedProduct.precioLista ?? selectedProduct.precio ?? selectedProduct.costo ?? 0);
                      const { precio: precioConCotizacion, tieneCotizacion: tieneCot, codigoMoneda } = calcularPrecioConCotizacion(selectedProduct, listaBase);
                      const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
                      
                      if (esMonedaEspecial) {
                        const precioLista = Number(selectedProduct.precioVenta ?? 0);
                        // Calcular precio convertido a ARS sin redondear intermedio para mostrar
                        const { precio: precioARS } = calcularPrecioConCotizacion(selectedProduct, precioLista, false);
                        const precioFinalARS = tieneCot ? precioARS : null;
                        return (
                          <>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                Precio de Lista: {formatearPrecioPorTipoMoneda(precioLista, selectedProduct, false, 5)}
                                {tieneCot && precioARS > 0 && (
                                  <span style={{ marginLeft: '8px', color: 'text.secondary', fontWeight: 400 }}>
                                    ({formatCurrency(precioARS)})
                                  </span>
                                )}
                              </Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 20,
                                  height: 20,
                                }}
                                title="Ver información de moneda"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="10" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="10" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                            {tieneCot ? (() => {
                              const ivaMultiplier = getIvaMultiplier(selectedProduct);
                              // Calcular precio final sin redondear hasta el final: precio * cotización * IVA
                              const precioFinalConIva = (precioFinalARS as number) * ivaMultiplier;
                              return (
                                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                                  Precio de Lista Final: {formatCurrency(round2(precioFinalConIva))}
                                </Typography>
                              );
                            })() : (
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                                  Precio de Lista Final: N/A (Cotización no disponible)
                                </Typography>
                                <Box
                                  component="span"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMonedaInfoDialogOpen(true);
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    width: 24,
                                    height: 24,
                                  }}
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <text x="4" y="16" fontSize="12" fill="#424242" fontWeight="bold">$</text>
                                    <text x="16" y="16" fontSize="12" fill="#424242" fontWeight="bold">¥</text>
                                    <path d="M 8 6 Q 12 4 16 6" stroke="#4caf50" strokeWidth="2" fill="none" strokeLinecap="round" />
                                    <path d="M 16 18 Q 12 20 8 18" stroke="#4caf50" strokeWidth="2" fill="none" strokeLinecap="round" />
                                    <path d="M 14 5 L 16 6 L 14 7" stroke="#4caf50" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M 10 19 L 8 18 L 10 17" stroke="#4caf50" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </Box>
                              </Box>
                            )}
                          </>
                        );
                      }
                      // Precio de Lista Final = Precio de Lista + IVA (sin aplicar descuento)
                      const ivaMultiplier = getIvaMultiplier(selectedProduct);
                      const precioListaFinal = listaBase * ivaMultiplier;
                      return (
                        <>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            Precio de Lista: {formatCurrency(listaBase)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                            Precio de Lista Final: {formatCurrency(precioListaFinal)}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {(() => {
                      const codProd = selectedProduct.codigoArticulo || selectedProduct.codigo;
                      const stockTotal = selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia ?? '-';
                      const nTotal = typeof stockTotal === 'number' ? stockTotal : Number(stockTotal);
                      const stockEnUbic = codProd && stockPorUbicacion[codProd];
                      return (
                        <>
                          <Chip label={`Cant.Total: ${stockTotal}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codProd, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia); }} sx={{ height: 22, bgcolor: 'white', borderColor: !Number.isNaN(nTotal) && nTotal > 0 ? 'success.main' : 'error.main', color: !Number.isNaN(nTotal) && nTotal > 0 ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          {(facturaVenta as FacturaVentaUI).ubicacion && codProd && stockPorUbicacion[codProd] !== undefined && (
                            <Chip label={`Cant.Deposito: ${stockPorUbicacion[codProd]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codProd, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockEnUbic === 'number' && stockEnUbic > 0) ? 'success.main' : 'error.main', color: (typeof stockEnUbic === 'number' && stockEnUbic > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          )}
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codProd, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia)} aria-label="Ver stock por ubicación">
                            <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />
                          </IconButton>
                        </>
                      );
                    })()}
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                    Categoría: {(() => {
                      const cod = selectedProduct.codigoRamo;
                      return ramoCache[String(cod)] || String(cod ?? '-');
                    })()}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              {(() => {
                const tipoMoneda = selectedProduct.tipoMoneda ?? selectedProduct.tipoMonedaArticulo ?? 0;
                const esMonedaEspecial = tipoMoneda === 1;
                
                return (
                  <>
                    <Box display="grid" gridTemplateColumns="1fr" justifyItems="center" mt={1}>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Cantidad:</Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        <IconButton 
                          onClick={() => setDialogQty(q => Math.max(0, (q || 0) - 1))}
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))}
                          sx={{ 
                            bgcolor: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'grey.300' : 'primary.main', 
                            color: 'white', 
                            '&:hover': { bgcolor: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'grey.300' : 'primary.dark' }, 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%',
                            '&.Mui-disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            }
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={dialogQty}
                          onChange={(e) => setDialogQty(Math.max(0, Number(e.target.value) || 0))}
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))}
                          sx={{ 
                            width: 90, 
                            '& .MuiInputBase-input': { 
                              textAlign: 'center', 
                              fontWeight: 700,
                              color: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'text.disabled' : 'text.primary',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }}
                          inputProps={{ min: 0 }}
                        />
                        <IconButton 
                          onClick={() => setDialogQty(q => (q || 0) + 1)}
                          disabled={!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))}
                          sx={{ 
                            bgcolor: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'grey.300' : 'primary.main', 
                            color: 'white', 
                            '&:hover': { bgcolor: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'grey.300' : 'primary.dark' }, 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%',
                            '&.Mui-disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Campo para editar precio de lista si el ramo permite editar precio */}
                    {(() => {
                      const codigoRamo = selectedProduct?.codigoRamo;
                      const puedeEditarPrecio = codigoRamo ? ramoEditaPrecioCache[codigoRamo] : false;
                      const noHayCotizacionParaEstaMoneda = esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0);
                      
                      if (puedeEditarPrecio) {
                        return (
                          <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Precio de Lista:</Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={dialogPrecioLista ?? ''}
                              onChange={(e) => {
                                const nuevoPrecio = Number(e.target.value);
                                if (!isNaN(nuevoPrecio) && nuevoPrecio >= 0) {
                                  setDialogPrecioLista(nuevoPrecio);
                                } else if (e.target.value === '') {
                                  setDialogPrecioLista(null);
                                }
                              }}
                              disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                              sx={{ 
                                width: 140, 
                                '& .MuiInputBase-input': { 
                                  textAlign: 'right',
                                  color: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'text.disabled' : 'text.primary',
                                },
                                '& .MuiInputBase-root.Mui-disabled': {
                                  bgcolor: 'grey.100',
                                }
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                    <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Descuento (%):</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={dialogDiscount}
                        onChange={(e) => setDialogDiscount(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                        disabled={!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))}
                        sx={{ 
                          width: 140, 
                          '& .MuiInputBase-input': { 
                            textAlign: 'right',
                            color: (!clienteSeleccionado || (esMonedaEspecial && !(cotizacionUSD && cotizacionUSD > 0))) ? 'text.disabled' : 'text.primary',
                          },
                          '& .MuiInputBase-root.Mui-disabled': {
                            bgcolor: 'grey.100',
                          }
                        }}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Box>
                  </>
                );
              })()}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeProductDialog} variant="outlined" color="inherit" sx={{ borderRadius: 999, px: 3 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleActualizarProducto} 
            variant="contained" 
            sx={{ borderRadius: 999, px: 3 }}
            disabled={!clienteSeleccionado || (() => {
              const t = selectedProduct?.tipoMoneda ?? selectedProduct?.tipoMonedaArticulo ?? 0;
              const esUSD = Number(t) === 1;
              const tieneCot = !!(cotizacionUSD && cotizacionUSD > 0);
              return esUSD && !tieneCot;
            })()}
          >
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>
      

      {/* Botón Guardar solo se muestra en la última ventana (summary) y cuando no es solo lectura */}
      {currentTab === 2 && !readOnly && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={loading || !facturaVenta.nroCliente}
            fullWidth
            sx={{ maxWidth: 480, borderRadius: 999 }}
          >
            {loading ? 'Guardando...' : saveButtonText}
          </Button>
        </Box>
      )}

      {/* Diálogo de Forma de Pago */}
      <Dialog
        open={formaDePagoDialogOpen}
        onClose={() => setFormaDePagoDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Seleccionar Forma de Pago
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFormaDePagoDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <RadioGroup
            value={(facturaVenta as FacturaVentaUI).formaDePago || ''}
            onChange={(e) => {
              onFacturaChange({
                ...facturaVenta,
                formaDePago: e.target.value,
              } as FacturaVenta);
              setFormaDePagoDialogOpen(false);
            }}
          >
            {formasDePago.map((forma) => {
              const isSelected = (facturaVenta as FacturaVentaUI).formaDePago === forma;
              return (
                <Box
                  key={forma}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: isSelected ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                    border: isSelected ? '1px solid rgba(156, 39, 176, 0.3)' : 'none',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(156, 39, 176, 0.15)' : 'action.hover',
                    },
                  }}
                >
                  <FormControlLabel
                    value={forma}
                    control={
                      <Radio
                        sx={{
                          color: isSelected ? '#9c27b0' : 'text.secondary',
                          '&.Mui-checked': {
                            color: '#9c27b0',
                          },
                        }}
                      />
                    }
                    label={forma}
                    sx={{
                      width: '100%',
                      m: 0,
                      px: 2,
                      py: 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1rem',
                        fontWeight: isSelected ? 500 : 400,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </RadioGroup>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Ubicación */}
      <Dialog
        open={ubicacionDialogOpen}
        onClose={() => {
          setUbicacionDialogOpen(false);
          setUbicacionSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Seleccionar Ubicación
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setUbicacionDialogOpen(false);
                setUbicacionSearchTerm('');
              }}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Campo de búsqueda */}
          <TextField
            fullWidth
            placeholder="Buscar ubicación..."
            value={ubicacionSearchTerm}
            onChange={(e) => setUbicacionSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {/* Lista de ubicaciones con RadioButtons */}
          <RadioGroup
            value={(facturaVenta as FacturaVentaUI).ubicacion?.match(/\((\d+)\)/)?.[1] || ''}
            onChange={(e) => {
              const selectedUbicacion = ubicaciones.find(
                (u) => String(u.nroUbicacion) === e.target.value
              );
              if (selectedUbicacion) {
                onFacturaChange({
                  ...facturaVenta,
                  ubicacion: `${selectedUbicacion.nombreUbicacion} (${selectedUbicacion.nroUbicacion})`,
                } as FacturaVenta);
                setUbicacionDialogOpen(false);
                setUbicacionSearchTerm('');
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredUbicaciones.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No hay ubicaciones disponibles
                </Typography>
              ) : (
                filteredUbicaciones.map((ubicacion) => {
                  const ubicacionValue = `${ubicacion.nombreUbicacion} (${ubicacion.nroUbicacion})`;
                  const isSelected = (facturaVenta as FacturaVentaUI).ubicacion === ubicacionValue;
                  return (
                    <Box
                      key={ubicacion.nroUbicacion}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <FormControlLabel
                        value={String(ubicacion.nroUbicacion)}
                        control={
                          <Radio
                            sx={{
                              color: 'text.secondary',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={ubicacionValue}
                        sx={{
                          width: '100%',
                          m: 0,
                          px: 2,
                          py: 1,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '1rem',
                            fontWeight: isSelected ? 500 : 400,
                          },
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setUbicacionDialogOpen(false);
              setUbicacionSearchTerm('');
            }} 
            variant="outlined"
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Información de Moneda */}
      <Dialog
        open={monedaInfoDialogOpen}
        onClose={() => setMonedaInfoDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Información de Moneda
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedProduct && (() => {
            const tipoMoneda = selectedProduct.tipoMoneda ?? selectedProduct.tipoMonedaArticulo ?? 0;
            const esMonedaEspecial = Number(tipoMoneda) === 1;
            const precioOriginal = Number(selectedProduct.precioVenta ?? 0);
            const monedaOriginal = esMonedaEspecial ? 'USD' : (selectedProduct.monedaOriginal ?? selectedProduct.codigoMonedaOriginal ?? 'ARS');
            const cotizacion = (cotizacionUSD && cotizacionUSD > 0) ? cotizacionUSD : (selectedProduct.cotizacion ?? selectedProduct.cotizacionAplicada ?? null);
            const cotizacionDisponible = cotizacion !== null && cotizacion !== undefined && cotizacion > 0;
            
            // Formatear como "92.36" (punto decimal)
            // Formatear precio: 5 decimales para monedas extranjeras, 2 para ARS
            const formatearPrecioMoneda = (precio: number, esMonedaExtranjera: boolean = false) => {
              const decimales = esMonedaExtranjera ? 5 : 2;
              return precio.toFixed(decimales);
            };
            
            return (
              <>
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio Mon. Original:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {monedaOriginal} {formatearPrecioMoneda(precioOriginal, esMonedaEspecial)}
                    </Typography>
                  </Box>
                  {esMonedaEspecial && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cotización Aplicada:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {cotizacionDisponible ? formatearPrecioMoneda(cotizacion) : 'No disponible'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {esMonedaEspecial && !cotizacionDisponible && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'warning.main',
                      border: '1px solid',
                      borderColor: 'warning.dark',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                      ⚠️ Cotización no disponible
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                      No hay ninguna cotización cargada. El precio se mantendrá en USD ({monedaOriginal} {formatearPrecioMoneda(precioOriginal, esMonedaEspecial)}).
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setMonedaInfoDialogOpen(false)} 
            variant="contained" 
            color="primary"
            fullWidth
            sx={{ borderRadius: 999, px: 3, textTransform: 'uppercase', fontWeight: 700 }}
          >
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Información del Subtotal Final */}
      <Dialog
        open={subtotalInfoDialogOpen}
        onClose={() => setSubtotalInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Detalle de Importes del Producto
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDetalleForInfo && (() => {
            const cantidad = selectedDetalleForInfo.cantidad || 0;
            const precioUnit = selectedDetalleForInfo.precioUnitario || 0;
            const desc = Number(selectedDetalleForInfo.descuento || 0);
            const codigoArticulo = selectedDetalleForInfo.codigoArticulo || '';
            const producto = productosDataCache[codigoArticulo];
            const ivaMultiplier = getIvaMultiplierByCodigo(codigoArticulo);
            const ivaRate = getIvaRateByCodigo(codigoArticulo);
            const ivaPorcentaje = (ivaRate * 100).toFixed(2);
            
            // Cálculos paso a paso
            const subtotalSinDescuento = cantidad * precioUnit;
            const montoDescuento = subtotalSinDescuento * (desc / 100);
            const subtotalBruto = subtotalSinDescuento - montoDescuento;
            const montoIVA = subtotalBruto * ivaRate;
            const subtotalConIVA = subtotalBruto + montoIVA;
            // Aplicar bonificaciones del cliente (se aplican secuencialmente)
            const bonifMultiplier = (1 - (bonif1 || 0) / 100) * (1 - (bonif2 || 0) / 100) * (1 - (bonif3 || 0) / 100);
            // Calcular montos de cada bonificación
            const montoBonif1 = subtotalConIVA * ((bonif1 || 0) / 100);
            const subtotalDespuesBonif1 = subtotalConIVA * (1 - (bonif1 || 0) / 100);
            const montoBonif2 = subtotalDespuesBonif1 * ((bonif2 || 0) / 100);
            const subtotalDespuesBonif2 = subtotalDespuesBonif1 * (1 - (bonif2 || 0) / 100);
            const montoBonif3 = subtotalDespuesBonif2 * ((bonif3 || 0) / 100);
            const totalBonificacion = montoBonif1 + montoBonif2 + montoBonif3;
            const subtotalFinal = subtotalConIVA * bonifMultiplier;
            // Bonificación aplicada sin impuestos
            const bonificacionAplicadaSinImpuestos = (subtotalConIVA - subtotalFinal) / (1 + ivaRate);
            // Subtotal Neto = Subtotal Bruto - Bonificación Aplicada (sin impuestos)
            const subtotalNeto = subtotalBruto - bonificacionAplicadaSinImpuestos;
            
            return (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {selectedDetalleForInfo.descripcionArticulo}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cantidad:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cantidad}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio de Lista:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(precioUnit)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Bruto del Artículo:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(subtotalSinDescuento)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>% Descuento del Artículo:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{desc}%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Bruto:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(subtotalBruto)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      Bonificación Aplicada
                      {((bonif1 > 0 || bonif2 > 0 || bonif3 > 0) && (
                        <> ({[bonif1, bonif2, bonif3].filter(b => b > 0).join('%+')}%)</>
                      ))}
                      :
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(bonificacionAplicadaSinImpuestos)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Neto:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(subtotalNeto)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'warning.dark' }}>
                      Subtotal Final:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'warning.dark' }}>
                      {formatCurrency(subtotalFinal)}
                    </Typography>
                  </Box>
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setSubtotalInfoDialogOpen(false)} 
            variant="contained" 
            color="primary"
            fullWidth
            sx={{ borderRadius: 999, px: 3, textTransform: 'uppercase', fontWeight: 700 }}
          >
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(stockInfoAnchorEl)}
        anchorEl={stockInfoAnchorEl}
        onClose={handleStockInfoClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 2, minWidth: 320, maxWidth: 480 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Stock por ubicación
            {stockInfoProducto && (
              <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {stockInfoProducto.codigoArticulo} - {stockInfoProducto.descripcion}
              </Typography>
            )}
          </Typography>
          {stockInfoLoading ? (
            <Typography variant="body2" color="text.secondary">Cargando...</Typography>
          ) : stockInfoData.length === 0 && (stockInfoProducto?.stockTotal === undefined || stockInfoProducto?.stockTotal === null) ? (
            <Typography variant="body2" color="text.secondary">No hay datos disponibles</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ubicación</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Asignado</TableCell>
                    <TableCell align="right">Disponible</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockInfoProducto?.stockTotal !== undefined && stockInfoProducto.stockTotal !== null && (
                    <TableRow sx={(theme) => ({ bgcolor: theme.palette.grey[100], '& td': { fontWeight: 600 } })}>
                      <TableCell>Stock (Total compañía)</TableCell>
                      <TableCell align="right">{Number(stockInfoProducto.stockTotal).toFixed(2)}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">—</TableCell>
                    </TableRow>
                  )}
                  {stockInfoData.map((row) => {
                    const ubicacionStr = (facturaVenta as FacturaVentaUI).ubicacion;
                    const nroUbicacionStr = ubicacionStr?.match(/\((\d+)\)/)?.[1];
                    const nroUbicacionSeleccionada = nroUbicacionStr ? parseInt(nroUbicacionStr, 10) : null;
                    const esUbicacionSeleccionada = nroUbicacionSeleccionada !== null && row.nroUbicacion === nroUbicacionSeleccionada;
                    return (
                      <TableRow
                        key={`${row.codigoArticulo}-${row.nroUbicacion}`}
                        sx={esUbicacionSeleccionada ? (theme) => ({ bgcolor: theme.palette.action.selected, '& td': { fontWeight: 600 } }) : undefined}
                      >
                        <TableCell>{row.nombreUbicacion}</TableCell>
                        <TableCell align="right">{Number(row.cantidadDeposito ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(row.cantidadAsignada ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(row.disponibleDeposito ?? 0).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default FacturaForm;